/**
 * `historyStartMarker` — a small rocket glyph under the first bar of a
 * symbol whose history genuinely starts there (the per-ticker floor was
 * reached, not the soft lookback cap). Says nothing: no label, no date, no
 * tooltip. Whether that first bar is an IPO, a listing change, or simply
 * where our data begins is not something the chart claims.
 *
 * Created programmatically by `ChartManager` with one point
 * `{ timestamp, value }` (only the x matters); it is not user-drawable.
 * Replaces the "Limited history available" pill (removed 2026-09-13).
 */
import { registerOverlay } from 'klinecharts'
import { NO_DEFAULT_FIGURES, registerOnce, type OverlayTemplateParam } from './registrationUtils'
import { ICON_CHIP_FIGURE, LUCIDE_ROCKET_PATHS, registerIconChipFigure } from './iconChipFigure'

export const HISTORY_START_MARKER_NAME = 'historyStartMarker'

/** Glyph box edge, px. */
export const HISTORY_START_MARKER_SIZE = 22
/** Gap between the glyph box and the pane's bottom edge, px. */
export const HISTORY_START_MARKER_BOTTOM_GAP = 10
/**
 * Blank space the pan guard leaves left of the first bar on a thin symbol,
 * px, so the glyph (centered on that bar) is never cut by the pane edge.
 */
export const HISTORY_START_MARKER_LEFT_ROOM = 32
/** Muted, like axis text: a landmark, not a signal. */
export const HISTORY_START_MARKER_COLOR = '#787b86'

export interface HistoryStartMarkerFigure {
  type: string
  attrs: { x: number; y: number; size: number; paths: string[] }
  styles: Record<string, unknown>
  ignoreEvent: boolean
}

/**
 * Pure figure builder. `x` is the first bar's pixel x; the glyph is
 * centered on it and sits just above the pane's bottom edge, like the
 * "first bar" markers on other charting platforms. It is a marker on that
 * bar's TIME, not a pane decoration: once the bar scrolls out of the pane
 * (either side) nothing is drawn, and it is never clamped to an edge.
 */
export function buildHistoryStartMarkerFigures(
  x: number,
  paneHeight: number,
  paneWidth: number,
): HistoryStartMarkerFigure[] {
  if (!Number.isFinite(x) || !Number.isFinite(paneHeight) || paneHeight <= 0) return []
  const size = HISTORY_START_MARKER_SIZE
  // Hidden once the bar is more than half a glyph outside the pane, so a
  // bar sitting on the edge neither flickers nor pins to it.
  if (!Number.isFinite(paneWidth) || x < -size / 2 || x > paneWidth + size / 2) return []
  return [
    {
      type: ICON_CHIP_FIGURE,
      attrs: {
        x: x - size / 2,
        y: paneHeight - HISTORY_START_MARKER_BOTTOM_GAP - size / 2,
        size,
        paths: LUCIDE_ROCKET_PATHS,
      },
      styles: {
        color: HISTORY_START_MARKER_COLOR,
        border: false,
        iconSize: size - 4,
        strokeWidth: 1.75,
      },
      ignoreEvent: true,
    },
  ]
}

export const registerHistoryStartMarkerOverlay = registerOnce(() => {
  registerIconChipFigure()
  registerOverlay({
    name: HISTORY_START_MARKER_NAME,
    totalStep: 2,
    lock: true,
    ...NO_DEFAULT_FIGURES,
    createPointFigures: ({
      coordinates,
      bounding,
    }: {
      coordinates: Array<{ x: number; y: number }>
      bounding: { height: number; width: number }
    }) => {
      const c = coordinates[0]
      if (!c) return []
      return buildHistoryStartMarkerFigures(c.x, bounding.height, bounding.width)
    },
  } as OverlayTemplateParam)
})
